"use client";

import { cn } from "@punchup/utils/cn";

/**
 * Base shimmer animation class - matches the neutral colors used in the real UI
 */
const shimmer = "animate-pulse bg-neutral-200";

/**
 * Skeleton for the fixed header bar
 * Matches VenuePageHeader - only shows logo and hamburger menu
 */
function HeaderSkeleton() {
  return (
    <header className="h-header-bar fixed top-app-banner left-0 right-0 w-full z-50 border-b border-neutral-200 bg-white">
      <div className="flex flex-row items-center justify-between w-full h-full px-3 md:px-4">
        {/* Logo placeholder */}
        <div
          className={cn(
            "h-[32px] md:h-[46px] w-[80px] md:w-[120px] rounded",
            shimmer
          )}
        />
        {/* Hamburger menu placeholder */}
        <div className={cn("h-10 w-10 rounded-lg", shimmer)} />
      </div>
    </header>
  );
}

/**
 * Skeleton for the top section with logo and navigation
 * Matches VenuePageTopSectionThemed structure
 */
function TopSectionSkeleton() {
  return (
    <div className="flex flex-col pt-2.5 pb-4 md:py-6 md:pt-4 lg:px-20 bg-neutral-100">
      {/* Logo */}
      <div className="flex flex-row justify-center items-center px-4">
        <div
          className={cn("h-[100px] md:h-[133px] w-[200px] rounded-md", shimmer)}
        />
      </div>
      {/* Navigation pills */}
      <div className="pt-4 md:pt-6">
        <div className="flex justify-center gap-2 md:gap-4 flex-wrap px-4 md:px-10">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={cn("h-10 w-20 md:w-24 rounded-full", shimmer)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for the carousel section
 * Matches VenueCarousel structure
 */
function CarouselSkeleton() {
  return (
    <div className="w-full">
      {/* Main carousel area */}
      <div className={cn("w-full h-[300px] md:h-[400px]", shimmer)} />
    </div>
  );
}

/**
 * Skeleton for a single show card
 * Matches the show card structure in VenueUpcomingShows
 */
function ShowCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-3 rounded-xl border border-neutral-200 bg-white">
      {/* Show image */}
      <div className={cn("w-full aspect-[4/3] rounded-lg", shimmer)} />
      {/* Comedian name */}
      <div className={cn("h-5 w-3/4 rounded", shimmer)} />
      {/* Date/time */}
      <div className={cn("h-4 w-1/2 rounded", shimmer)} />
      {/* Price/button area */}
      <div className="flex justify-between items-center mt-1">
        <div className={cn("h-4 w-16 rounded", shimmer)} />
        <div className={cn("h-9 w-24 rounded-full", shimmer)} />
      </div>
    </div>
  );
}

/**
 * Skeleton for a single show row in list view.
 * Matches the horizontal TourDateRow layout (thumb → text stack → button).
 */
function TourDateRowSkeleton() {
  return (
    <div className="flex flex-row items-center gap-4 p-3 rounded-xl border border-neutral-200 bg-white">
      <div className={cn("h-16 w-16 rounded-lg shrink-0", shimmer)} />
      <div className="flex flex-col gap-2 flex-1">
        <div className={cn("h-5 w-1/2 rounded", shimmer)} />
        <div className={cn("h-4 w-1/3 rounded", shimmer)} />
      </div>
      <div className={cn("h-9 w-24 rounded-full shrink-0", shimmer)} />
    </div>
  );
}

/**
 * Skeleton for the upcoming shows section
 * Matches VenueUpcomingShows structure
 */
function UpcomingShowsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {/* Section title */}
      <div className={cn("h-7 w-40 rounded", shimmer)} />
      {/* Shows grid - matches the responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <ShowCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for the description section
 */
function DescriptionSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className={cn("h-6 w-24 rounded", shimmer)} />
      <div className={cn("h-4 w-full rounded", shimmer)} />
      <div className={cn("h-4 w-full rounded", shimmer)} />
      <div className={cn("h-4 w-2/3 rounded", shimmer)} />
    </div>
  );
}

/**
 * Skeleton for info tags
 */
function InfoTagsSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className={cn("h-8 w-28 rounded-full", shimmer)} />
      ))}
    </div>
  );
}

/**
 * Skeleton for subscription banner
 * Matches VenueSubscriptionBannerThemed structure
 */
function SubscriptionBannerSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-neutral-200 bg-neutral-50">
      <div className={cn("h-6 w-48 rounded", shimmer)} />
      <div className={cn("h-4 w-full rounded", shimmer)} />
      <div className={cn("h-11 w-full rounded-full", shimmer)} />
    </div>
  );
}

/**
 * Skeleton for video player
 */
function VideoSkeleton() {
  return <div className={cn("w-full aspect-video rounded-xl", shimmer)} />;
}

/**
 * Skeleton for map card
 * Matches VenueMapCard structure
 */
function MapSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className={cn("h-6 w-20 rounded", shimmer)} />
      <div className={cn("w-full h-[200px] rounded-xl", shimmer)} />
      <div className={cn("h-4 w-2/3 rounded", shimmer)} />
    </div>
  );
}

/**
 * Skeleton for venue hours
 */
function HoursSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className={cn("h-6 w-16 rounded", shimmer)} />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex justify-between">
          <div className={cn("h-4 w-20 rounded", shimmer)} />
          <div className={cn("h-4 w-28 rounded", shimmer)} />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for social links
 */
function SocialLinksSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className={cn("h-6 w-24 rounded", shimmer)} />
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={cn("w-12 h-12 rounded-md", shimmer)} />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for footer
 * Matches VenuePageFooter - light gray background with centered content
 */
function FooterSkeleton() {
  return (
    <footer className="px-4 md:px-8 pt-8 md:pt-4 pb-8 md:pb-12 bg-[#F0F0F0]">
      <div className="md:px-[120px]">
        <div className="flex flex-col items-start md:items-center justify-start md:pt-8 gap-y-6">
          <div className="flex flex-col items-start justify-start md:items-center gap-y-3">
            {/* Venue logo placeholder (faded) */}
            <div
              className={cn(
                "h-[120px] w-[120px] rounded-md opacity-20",
                shimmer
              )}
            />
            {/* Copyright and links placeholder */}
            <div className={cn("h-4 w-48 rounded", shimmer)} />
          </div>
          {/* Powered by Punchup placeholder */}
          <div className="flex flex-row items-center gap-3">
            <div className={cn("h-4 w-20 rounded", shimmer)} />
            <div className={cn("h-[18px] w-[142px] rounded", shimmer)} />
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Skeleton for the /v/ route header section
 * Matches the custom header layout with logo, venue name, and buttons
 */
function VRouteHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-y-4">
      {/* Header */}
      <header className="flex flex-col items-start md:flex-row md:justify-between md:items-center gap-4 px-4 md:px-10 lg:px-[120px]">
        {/* Logo + Name/Address grid */}
        <div className="grid grid-cols-[70px_1fr] md:grid-cols-[96px_1fr] gap-3 md:gap-5">
          {/* Logo placeholder */}
          <div
            className={cn(
              "rounded-xl aspect-square w-[70px] h-[70px] md:w-24 md:h-24",
              shimmer
            )}
          />
          {/* Name and address */}
          <div className="flex flex-col justify-center h-full gap-y-1.5">
            <div className={cn("h-6 md:h-8 w-48 md:w-64 rounded", shimmer)} />
            <div className={cn("h-4 md:h-5 w-36 md:w-48 rounded", shimmer)} />
          </div>
        </div>
        {/* Follow + Share buttons */}
        <div className="grid grid-cols-[1fr_100px] md:flex md:flex-row h-full w-full md:w-[unset] items-center gap-x-4 md:gap-x-6">
          <div className={cn("h-10 w-full md:w-32 rounded-full", shimmer)} />
          <div className={cn("h-10 w-[100px] rounded-full", shimmer)} />
        </div>
      </header>
    </div>
  );
}

/**
 * Skeleton for the /v/ route navigation tabs
 * Matches VenuePageNav structure with horizontal scrollable pills
 */
function VRouteNavSkeleton() {
  return (
    <div className="px-4 md:mx-10 lg:mx-[120px]">
      <div className="flex gap-2 md:gap-3 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={cn(
              "h-10 w-20 md:w-24 rounded-full flex-shrink-0",
              shimmer
            )}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Full venue page skeleton for /v/ route
 * Only includes body content since the header and navigation tabs
 * are already rendered in the layout (layout.tsx)
 */
export function VRoutePageSkeleton() {
  return (
    <div className="flex flex-col pb-16 gap-y-8 md:gap-y-16 lg:px-20">
      {/* Carousel + Upcoming Shows Section */}
      <section>
        <div className="md:px-10 pb-4">
          <CarouselSkeleton />
        </div>
        <div className="px-4 md:px-10">
          <UpcomingShowsSkeleton />
        </div>
      </section>

      {/* Two-column content grid */}
      <section className="px-4 md:px-10 gap-y-8 flex flex-col md:grid md:grid-cols-[1fr_384px] md:gap-x-10">
        {/* Left column */}
        <div className="flex flex-col w-full gap-y-8">
          <DescriptionSkeleton />
          <div className="block w-full h-px border-t border-neutral-200" />
          <InfoTagsSkeleton />
          <div className="block w-full h-px border-t border-neutral-200" />
          <SubscriptionBannerSkeleton />
          <VideoSkeleton />
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-y-6">
          <MapSkeleton />
          <HoursSkeleton />
          <SocialLinksSkeleton />
        </div>
      </section>
    </div>
  );
}

/**
 * Full venue page skeleton - matches the exact structure of the themed venue page
 * This shows immediately while data loads via Suspense streaming
 */
export function VenuePageSkeleton() {
  return (
    <div className="flex flex-col">
      {/* Fixed Header */}
      <HeaderSkeleton />

      {/* Main content with header offset - matches VenueLayoutContent structure */}
      <div className="pt-[var(--header-height)] min-h-screen flex flex-col">
        <div className="flex flex-col md:pb-8 gap-y-5 md:gap-y-8">
          {/* Body content wrapper */}
          <div className="flex flex-col pb-8 md:pb-16">
            {/* Top Section with Logo and Nav */}
            <TopSectionSkeleton />

            {/* VenuePage content */}
            <div className="flex flex-col">
              {/* Carousel */}
              <CarouselSkeleton />

              {/* Main content area */}
              <div className="flex flex-col gap-y-8 md:gap-y-16 lg:px-20 pt-4 md:pt-4">
                {/* Upcoming Shows Section */}
                <section>
                  <div className="px-4 md:px-10">
                    <UpcomingShowsSkeleton />
                  </div>
                </section>

                {/* Two-column content grid */}
                <section className="px-4 md:px-10 gap-y-8 flex flex-col md:grid md:grid-cols-[1fr_384px] md:gap-x-10">
                  {/* Left column */}
                  <div className="flex flex-col w-full gap-y-8">
                    <DescriptionSkeleton />
                    <div className="block w-full h-px border-t border-neutral-200" />
                    <InfoTagsSkeleton />
                    <div className="block w-full h-px border-t border-neutral-200" />
                    <SubscriptionBannerSkeleton />
                    <VideoSkeleton />
                  </div>

                  {/* Right column */}
                  <div className="flex flex-col gap-y-6">
                    <MapSkeleton />
                    <HoursSkeleton />
                    <SocialLinksSkeleton />
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - outside the scrollable area, same as VenueLayoutContent */}
      <FooterSkeleton />
    </div>
  );
}

// Export individual skeletons for per-component use
export {
  HeaderSkeleton,
  TopSectionSkeleton,
  CarouselSkeleton,
  ShowCardSkeleton,
  TourDateRowSkeleton,
  UpcomingShowsSkeleton,
  DescriptionSkeleton,
  InfoTagsSkeleton,
  SubscriptionBannerSkeleton,
  VideoSkeleton,
  MapSkeleton,
  HoursSkeleton,
  SocialLinksSkeleton,
  FooterSkeleton,
};

export default VenuePageSkeleton;
